import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Layout, Row, Col, Typography, Card, Avatar, Button, notification } from 'antd';
import { 
  DashboardFilled,
  ApiFilled,
  LinkOutlined,
  ProfileFilled,
  MergeFilled,
  CalculatorFilled,
  PieChartFilled,
  UserOutlined,
  UsergroupAddOutlined,
  SettingOutlined,
  MessageOutlined,
  SafetyCertificateOutlined,
  RobotFilled,
  FileTextFilled,
  ApartmentOutlined
} from '@ant-design/icons';
import { getAssetia } from "../../API/assetia";
import { useAuth } from "../Context/AuthContext";
import isoHome from "./assets/iso-home.png";
import chainIcon from "../Common/PageTitle/assets/chain.png";
import ChartSVG from './components/ChartSVG';
import './Home.css';

const { Content } = Layout;
const { Title, Paragraph, Text } = Typography;

const Home = () => {
  const { hasPermission, userData } = useAuth();
  // Consulta permisos: Superusuario  
  const isSuperuser = userData?.is_superuser === true;
  // Consulta permisos: Tiene rol asignado  
  const hasAnyRole = userData?.roles?.length > 0;

  const canAssetsRead = userData ? hasPermission("assets", "read") : false;
  const canDependenciesRead = userData ? hasPermission("dependencies", "read") : false;
  const canControlsRead = userData ? hasPermission("controls", "read") : false;
  const canAssignmentRead = userData ? hasPermission("assignment", "read") : false;
  const canEvaluationRead = userData ? hasPermission("evaluation", "read") : false;
  const canGapsRead = userData ? hasPermission("gaps", "read") : false;
  const canTasksRead = userData ? hasPermission("tasks", "read") : false;

  const [api, contextHolder] = notification.useNotification();
  const location = useLocation();

  // Assetia
  const [tips, setTips] = useState([]);
  const [currentTipIndex, setCurrentTipIndex] = useState(-1);
  // Dashboard General
  const [dashboardGeneral, setDashboardGeneral] = useState(() => {
  const stored = localStorage.getItem('dashboardGeneral');
    return stored ? JSON.parse(stored) : {};
  });

  const hasAssets = dashboardGeneral?.assets?.count > 0;
  const hasDependencies = dashboardGeneral?.dependencies?.count > 0;

  const canAccessAssets =
    isSuperuser ||
    (hasAnyRole && canAssetsRead) ||
    hasAssets;

  const canSeeDependencies =
    isSuperuser ||
    (hasAnyRole && canDependenciesRead) ||
    hasDependencies;

  useEffect(() => {
    if (
  location.state?.permissionDenied &&
  !canAccessAssets
) {
  api.error({
    message: 'Acceso denegado',
    description: 'No tienes permisos para acceder a esta función',
    duration: 4.5,
    placement: 'topRight',
  });
}
    
  if (!isSuperuser && !hasAnyRole) {
    api.warning({
      message: 'Sin permisos asignados',
      description: 'Contacta al administrador para obtener acceso.',
      placement: 'topRight',
      duration: 3,
    });
  }

  const fetchAssetia = async () => {
    try {
      const res = await getAssetia();
      const homeTips = res.home || [];
      homeTips.sort((a, b) => a.index - b.index);
      setTips(homeTips);
    } catch (err) {
      console.error("Error cargando Assetia:", err);
    }
  };

    fetchAssetia();
  }, [location.state, isSuperuser, hasAnyRole, api]);

  const handleNextTip = () => {
    if (currentTipIndex + 1 < tips.length) {
      setCurrentTipIndex(currentTipIndex + 1);
    } else {
      // Volver al inicio
      setCurrentTipIndex(-1);
    }
  };

  const renderAssistantContent = () => {
    if (currentTipIndex === -1) {
      return (
        <>
          <Paragraph className="assistant-text" type="secondary">
            Tu asistente personal. ¿Necesitas ayuda? Puedo darte consejos para optimizar tu trabajo.
          </Paragraph>
          <a href="#" className="assistant-link" onClick={(e) => { e.preventDefault(); handleNextTip(); }}>
            Ver consejos &rarr;
          </a>
        </>
      );
    } else {
      const tip = tips[currentTipIndex];
      return (
        <>
          <Paragraph className="assistant-text" type="secondary">
            {tip.description}
          </Paragraph>
          <a className="assistant-link" onClick={handleNextTip}>
            Siguiente consejo &rarr;
          </a>
        </>
      );
    }
  };

  const menuItems = [
    {
      title: 'Inventario de Activos Críticos',
      icon: <ApiFilled />,
      description: 'Consulta los activos críticos definidos.',
      to: '/assets',
      color: 'general',
      canRead: canAssetsRead,
    },
    {
      title: 'Inventario de Dependencias',
      icon: <img src={chainIcon} alt="Dependencias" style={{ width: 20, height: 20 }} />,
      description: 'Administra la infraestructura de soporte de los activos críticos.',
      to: '/dependencies',
      color: 'general',
      canRead: canDependenciesRead,
    },
    {
      title: 'Inventario de Controles',
      icon: <ProfileFilled />,
      description: 'Definí los controles que podrían ser asignados a los activos críticos.',
      to: '/controls',
      color: 'general',
      canRead: canControlsRead,
    },
    {
      title: 'Asignación de Controles',
      icon: <MergeFilled />,
      description: 'Asigna controles a los activos críticos para que sean evaluados.',
      to: '/assignment',
      color: 'general',
      canRead: canAssignmentRead,
    },
    {
      title: 'Evaluación de Controles',
      icon: <CalculatorFilled />,
      description: 'Evalúa el nivel de madurez y efectividad de los controles.',
      to: '/evaluation',
      color: 'general',
      canRead: canEvaluationRead,
    },
    {
      title: 'Gaps',
      icon: <PieChartFilled />,
      description: 'Gestiona los GAPs identificados durante la evaluación de controles.',
      to: '/gaps',
      color: 'general',
      canRead: canGapsRead,
    },
    {
      title: 'Tareas',
      icon: <FileTextFilled />,
      description: 'Actualiza el estado de las tareas asignadas y delegadas.',
      to: '/tasks',
      color: 'general',
      canRead: canTasksRead,
    },
    {
      title: 'Procesos',
      icon: <ApartmentOutlined />,
      description: 'Administra los procesos, subprocesos y sus diagramas de flujo.',
      to: '/processes',
      color: 'general',
      canRead: canDependenciesRead,
    },
    {
      title: 'Perfil',
      icon: <UserOutlined />,
      description: 'Administra tu perfil personal y credenciales.',
      to: '/userprofile',
      color: 'general2',
      canRead: true
    },
    {
      title: 'Gestión de Usuarios',
      icon: <UsergroupAddOutlined />,
      description: 'Configura los usuarios y roles de acceso al sistema.',
      to: '/user-management',
      color: 'general2',
      onlySuperuser: true,
    },
    {
      title: 'Configuraciones',
      icon: <SettingOutlined />,
      description: 'Accede a la configuración de los parámetros del sistema.',
      to: '/settings',
      color: 'general2',
      onlySuperuser: true,
    },
    {
      title: 'Licencias',
      icon: <SafetyCertificateOutlined />,
      description: 'Consulta la licencia contratada.',
      to: '/licences',
      color: 'general2',
      onlySuperuser: true,
    }
  ];

  return (
    <>
    {contextHolder}
    <Layout>
      <Content>
        <Card bordered={false} className="light-card welcome-card" style={{marginBottom: 20}}>
          <div className="home-card-content">
            <img src={isoHome} alt="Logo" className="iso-logo" />

            <div className="text-content">
              <Title level={2} style={{ color: "#006939", margin: 0 }}>
                Bienvenido a Exa Asset Control Compliance
              </Title>
              <Paragraph style={{ fontSize: 16, margin: 0 }}>
                Gestione los controles de sus activos críticos, supervisando el cumplimiento normativo y accediendo a métricas clave en tiempo real desde una única plataforma.
              </Paragraph>
            </div>
          </div>
        </Card>

        {/* Contenido principal: Grid responsivo */}
        <Row gutter={[24, 24]}>
          {/* Tarjeta Principal: Dashboard */}
          <Col xs={24} lg={16}>
            <Card className="dashboard-card dashboard-main-card">
              <div className="card-header-icon">
                <div className="icon-container-general">
                  <DashboardFilled />
                </div>
                <div>
                  <Title level={4} style={{ margin: 0 }}>Tablero de Gestión</Title>
                  <Text type="secondary">Visualiza el estado general y estadísticas de los controles.</Text>
                </div>
              </div>
              <ChartSVG />
              <Link to="/dashboard">
                <Button 
                  type="primary" 
                  size="large" 
                  block 
                  className="dashboard-button" 
                  disabled={!isSuperuser && (!hasAnyRole)}
                >
                    Consultar Tablero
                </Button>
              </Link>
            </Card>
          </Col>

          {/* Tarjeta Lateral: Asistente Personal */}
          <Col xs={24} lg={8} style={{ display: 'flex' }}>
            <Card className="assistant-card">
              <div className="icon-container-robot icon-container-large">
                <RobotFilled className="robot-icon" />
              </div>
              <h2>
                <span className="assetia">Hola soy ASSET</span>
                <span className="ia">IA</span>
              </h2>
              {renderAssistantContent()}
            </Card>
          </Col>

          {/* Tarjetas de Herramientas */}
          {menuItems
            .filter(item => !item.onlySuperuser || isSuperuser)
            .map((item) => {
             const canClick = item.to === '/assets'
                ? canAccessAssets
                : item.to === '/dependencies'
                  ? canSeeDependencies
                  : isSuperuser || (hasAnyRole && item.canRead);

              const cardContent = (
                <Card
                  hoverable={canClick}
                  className={`light-card tool-card ${!canClick ? 'disabled-card' : ''}`}
                  style={!canClick ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
                >
                  <div className={`icon-container-${item.color}`}>
                    {item.icon}
                  </div>
                  <Title level={5} style={{ marginTop: '16px', marginBottom: '4px' }}>
                    {item.title}
                  </Title>
                  <Paragraph type="secondary">{item.description}</Paragraph>
                </Card>
              );

              return (
                <Col xs={24} sm={12} md={8} key={item.title}>
                  {canClick ? <Link to={item.to}>{cardContent}</Link> : cardContent}
                </Col>
              );
            })}
        </Row>
      </Content>
    </Layout>
    </>
  );
};

export default Home;
